import apiClient from './apiClient';
import { API_ENDPOINTS } from '../config/apiConfig';
import { RtuLicenseOptions } from '../types/RtuLicense';

export interface GeneratedRtuLicenseDownload {
  blob: Blob;
  filename: string;
}

const parseContentDispositionFilename = (header: string | undefined): string | null => {
  const value = String(header ?? '');
  const encoded = value.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (encoded) {
    try {
      return decodeURIComponent(encoded);
    } catch {
      return encoded;
    }
  }
  const quoted = value.match(/filename="([^"]+)"/i)?.[1];
  if (quoted) {
    return quoted;
  }
  const bare = value.match(/filename=([^;]+)/i)?.[1];
  return bare ? bare.trim() : null;
};

export async function parseBlobApiError(error: unknown, fallback: string): Promise<string> {
  const responseData = (error as { response?: { data?: unknown } })?.response?.data;
  if (responseData instanceof Blob) {
    const mime = String(responseData.type || '').toLowerCase();
    if (mime.includes('json') || mime.includes('text') || mime === '') {
      try {
        const text = await responseData.text();
        const parsed = JSON.parse(text) as { message?: string };
        if (parsed?.message && typeof parsed.message === 'string' && parsed.message.trim()) {
          return parsed.message.trim();
        }
      } catch {
        // Fall through to generic message.
      }
    }
  }
  const message = (error as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message
    || (error as { message?: string })?.message;
  if (typeof message === 'string' && message.trim()) {
    return message.trim();
  }
  return fallback;
}

export async function generateRtuLicense(
  rtuId: number | string,
  options: RtuLicenseOptions,
  fallbackFilename?: string,
): Promise<GeneratedRtuLicenseDownload> {
  const response = await apiClient.post(
    API_ENDPOINTS.RTU.GENERATE_LICENSE(rtuId),
    options,
    { responseType: 'blob' },
  );
  const headerName = parseContentDispositionFilename(response.headers?.['content-disposition']);
  const filename = headerName || fallbackFilename || `rtu-${rtuId}.lic`;
  const blob = new Blob([response.data], { type: 'application/octet-stream' });
  return { blob, filename };
}

export function triggerBrowserDownload(blob: Blob, filename: string): void {
  const objectUrl = window.URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = filename;
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    window.URL.revokeObjectURL(objectUrl);
  }
}
